import { useCallback, useState } from 'react';
import type {
  ChangeEvent,
  FocusEvent,
  HTMLAttributes,
  KeyboardEvent,
} from 'react';
import { useTranslations } from 'next-intl';
import { UiLegacy } from '@silvertours/front-legacy-shared';
import { analytics } from '@silvertours/front-entities';
import {
  StyledIcon,
  Wrapper,
  StyledLabel,
  CompactInput,
  StyledErrorMessage,
  StyledTooltip,
} from './AgeInput.styles';
import {
  DEFAULT_AGE,
  DEFAULT_AGE_RANGE,
  MAX_AGE,
  MIN_AGE,
  useSearchContext,
} from '../context';

type Props = {
  value: number;
  onChange: (age: number) => void;
} & Pick<HTMLAttributes<HTMLDivElement>, 'className'>;

const AgeInput = ({ value, onChange, className }: Props) => {
  const t = useTranslations('features.stageLegacy.stage.searchForm');
  const { state, setError } = useSearchContext();
  const [displayValue, setDisplayValue] = useState<string>(() => {
    if (value >= DEFAULT_AGE_RANGE.min && value <= DEFAULT_AGE_RANGE.max) {
      return '';
    }
    return value.toString();
  });
  const error = state?.ageError;

  const resetError = useCallback(() => {
    if (error) {
      setError({ kind: 'ageError', message: '' });
    }
  }, [error, setError]);

  const resetAge = useCallback(() => {
    resetError();
    setDisplayValue('');
    onChange(DEFAULT_AGE);
  }, [onChange, resetError]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const { value: currentValue } = event.target;
      if (currentValue === '') {
        resetAge();
        return;
      }

      const numericValue = Number(currentValue);
      if (
        Number.isNaN(numericValue) ||
        (numericValue >= DEFAULT_AGE_RANGE.min &&
          numericValue <= DEFAULT_AGE_RANGE.max)
      ) {
        resetAge();
      } else if (Number(numericValue) < MIN_AGE) {
        setError({
          kind: 'ageError',
          message: t('errorTexts.age', { min: MIN_AGE }),
        });
      } else {
        resetError();
      }
    },
    [t, setError, resetAge, resetError],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value: currentValue } = event.target;
      if (currentValue === '') {
        resetAge();
        return;
      }

      const numericValue = Number(currentValue);

      // We do not want to trigger tracking while typing the first digit
      if (numericValue >= 10) {
        analytics.gtm.trackAgeInputChange(currentValue);
      }
      if (numericValue >= MIN_AGE && numericValue <= MAX_AGE) {
        onChange(numericValue);
      }
    },
    [onChange, resetAge],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      // Allow only numbers and control keys
      if (
        !(
          (event.key >= '0' && event.key <= '9') ||
          event.key === 'Backspace' ||
          event.key === 'Delete' ||
          event.key === 'ArrowLeft' ||
          event.key === 'ArrowRight' ||
          event.key === 'Tab'
        )
      ) {
        event.preventDefault();
      }
    },
    [],
  );

  if (!state) {
    return null;
  }

  const handleFocus = () => {
    analytics.gtm.trackEditAgeClicked();
    analytics.generic.trackEditDriverAgeClick();
  };

  const hasError = !!error;

  return (
    <Wrapper className={className}>
      <StyledTooltip
        content={<UiLegacy.Markup content={t('age.tooltip.text')} />}
        trigger={<StyledIcon />}
        aria-label={t('age.tooltip.label')}
      />
      <StyledLabel htmlFor="customerAge">{t('age.input.label')}</StyledLabel>
      <CompactInput
        id="customerAge"
        placeholder={t('age.defaultAge')}
        type="text"
        defaultValue={displayValue}
        inputMode="numeric"
        maxLength={2}
        aria-invalid={hasError ? 'true' : undefined}
        aria-errormessage={hasError ? 'age-error' : undefined}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        $isInvalid={hasError}
      />
      {hasError && (
        <StyledErrorMessage id="age-error">{error}</StyledErrorMessage>
      )}
    </Wrapper>
  );
};

export { AgeInput };
